import { AuthProvider } from '@/auth/providers/auth.provider';
import { AUTH_API, AUTH_STORAGE_KEYS } from '@/auth/values/auth.values';
import { LocalStorageService } from '@/services/local-storage.service';
import axios, { Method } from 'axios';
import log from 'js-logger';
import { REQUEST_INFO, SERVER_HOSTNAME, SERVER_TYPE } from '../enums/request.enum';

class RequestProviderClass {

  accessToken;
  handleRefresh: boolean = true;
  // Login, startup, and token refresh can happen together. They share one request so they refresh one Media session.
  private mediaSessionPromise: Promise<any> | null = null;

  private async refreshAccessToken() {
    const refreshToken = LocalStorageService.getItem(AUTH_STORAGE_KEYS.ED_REFRESH_TOKEN);
    const body = new URLSearchParams();
    body.append('grant_type', 'refresh_token');
    body.append('refresh_token', refreshToken);
    const hostname = process.env[SERVER_HOSTNAME[SERVER_TYPE.AUTH]];
    const path = `${hostname}/${AUTH_API.REFRESH_TOKEN}`;
    const response = await axios({
      method: 'POST',
      url: path,
      data: body.toString(),
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        auth: {
        username: REQUEST_INFO.BASIC_AUTH_USER,
        password: REQUEST_INFO.BASIC_AUTH_PASSWORD
      }
    });
    return response;
  }

  public async get(url: string, params: any = {}, headers: any = {}, timeout: number = 0): Promise<any> {
    return this.request('GET', url, {}, params, headers, timeout);
  }

  public async post(url: string, data: any = {}, params: any = {}, headers: any = {}, timeout: number = 0): Promise<any> {
    return this.request('POST', url, data, params, headers, timeout);
  }

  public async put(url: string, data: any = {}, params: any = {}, headers: any = {}, timeout: number = 0): Promise<any> {
    return this.request('PUT', url, data, params, headers, timeout);
  }

  public async remove(url: string, data: any = {}, timeout: number = 0): Promise<any> {
    return this.request('DELETE', url, data, {}, {}, timeout);
  }

  public async request(
    method: Method,
    url: string,
    data: any = {},
    params: any = {},
    headersInput: any = {},
    timeout: number = 0,
    server: SERVER_TYPE = SERVER_TYPE.COMMON,
    returnAllInfo: boolean = false
    ): Promise<any> {

      const auth = {
        username: REQUEST_INFO.BASIC_AUTH_USER,
        password: REQUEST_INFO.BASIC_AUTH_PASSWORD
      };
      const hostname = process.env[SERVER_HOSTNAME[server]];
      const path = `${hostname}/${url}`;
      const options = server === SERVER_TYPE.AUTH ? { auth } : {};
      this.accessToken = (this.handleRefresh) ? LocalStorageService.getItem(AUTH_STORAGE_KEYS.ED_JWT) : this.accessToken;

      try {

        const headers = !this.accessToken ? {} : { Authorization: `Bearer ${this.accessToken}` };
        Object.assign(headers, headersInput);

        return await this.requestByAxios(method,
          path,
          data,
          params,
          headers,
          options,
          returnAllInfo,
          timeout);

      } catch(error) {

        /* Permission denied and token expired */
        if(this.handleRefresh && error?.response?.status === 401 && !AuthProvider.getIsTokenActive(this.accessToken) ) {

          try {
            const { data } = await this.refreshAccessToken();
            LocalStorageService.setItem(AUTH_STORAGE_KEYS.ED_JWT, data.access_token);
            LocalStorageService.setItem(AUTH_STORAGE_KEYS.ED_REFRESH_TOKEN, data.refresh_token);
            // The refreshed sign-in also needs refreshed Media access before a private file is loaded again.
            await this.issueMediaSession().catch((error) =>
              log.warn('Media session refresh failed', { status: error?.response?.status })
            );
            const headers = !data.access_token ? {} : { Authorization: `Bearer ${data.access_token}` };
            Object.assign(headers, headersInput);
            return this.requestByAxios(method,
              path,
              data,
              params,
              headers,
              options,
              returnAllInfo,
              timeout);
          } catch(errorRefresh) {
            LocalStorageService.removeItem(AUTH_STORAGE_KEYS.ED_JWT);
            LocalStorageService.removeItem(AUTH_STORAGE_KEYS.ED_REFRESH_TOKEN);
            window.location.href = '/';
          }

        } else {
          throw error.response;
        }

      }

  }

  public async requestByAxios(
      method: Method,
      path: string,
      data: any = {},
      params: any = {},
      headers: any = {},
      options: any = {},
      returnAllInfo: boolean = false,
      timeout: number = 0
      ): Promise<any> {

      const response = await axios({
          method,
          url: path,
          data,
          params,
          headers,
          ...options,
          timeout
        });

     return returnAllInfo ? response : (response.data || response);
  }

  public async requestPublicUrl(url: string, params: any = {}): Promise<any> {
    return this.requestByAxios('GET',
      url,
      params);
  }

  public async requestAuth(method: Method, url: string, data: any = {}, params: any = {}, headers: any = {},
                           timeout: number = 0): Promise<any> {
    return this.request(method, url, data, params, headers, timeout, SERVER_TYPE.AUTH);
  }

  public async requestPublic(method: Method, url: string, data: any = {}, params: any = {}, headers: any = {},
                             timeout: number = 0): Promise<any> {
    return this.request(method, url, data, params, headers, timeout, SERVER_TYPE.PUBLIC);
  }

  public getTokenHeader() {
    const accessToken = LocalStorageService.getItem(AUTH_STORAGE_KEYS.ED_JWT);
    return !accessToken ? {} : { Authorization: `Bearer ${accessToken}` };
  }

  /**
   * Opens the temporary Media access used by images, audio, video, PDFs, and slides in the web application.
   * The browser receives and keeps the cookie; Vue never reads or stores its value. Reusing an in-progress request
   * prevents login, startup, and token refresh from creating competing Media sessions.
   */
  public async issueMediaSession(): Promise<any> {
    if (this.mediaSessionPromise) return this.mediaSessionPromise;

    this.mediaSessionPromise = this.requestMediaSession('POST', { runtime: 'web' })
      .catch(async (error) => {
        if (error?.response?.status !== 401) throw error;
        // The server cleared an expired or unknown cookie. Retry once so the browser can receive a new one.
        return this.requestMediaSession('POST', { runtime: 'web' });
      })
      .finally(() => {
        this.mediaSessionPromise = null;
      });

    return this.mediaSessionPromise;
  }

  public async revokeMediaSession(): Promise<void> {
    // Send this before removing local authentication. Logout continues even if the cleanup request fails.
    await this.requestMediaSession('DELETE', {});
  }

  /**
   * Requests a private slide entry point. The user token authorizes this controlled request and browser credentials
   * let Media associate the resulting slide URL with the existing Media cookie.
   */
  public async postMedia(url: string, data: any = {}): Promise<any> {
    const hostname = process.env[SERVER_HOSTNAME[SERVER_TYPE.COMMON]];
    const token = LocalStorageService.getItem(AUTH_STORAGE_KEYS.ED_JWT);
    return this.requestByAxios('POST', `${hostname}/${url}`, data, {},
      token ? { Authorization: `Bearer ${token}` } : {}, { withCredentials: true });
  }

  private async requestMediaSession(method: Method, data: any): Promise<any> {
    // Browser credentials let the browser receive a new cookie and return its current cookie for refresh or logout.
    const hostname = process.env[SERVER_HOSTNAME[SERVER_TYPE.COMMON]];
    const token = LocalStorageService.getItem(AUTH_STORAGE_KEYS.ED_JWT);
    const response = await this.requestByAxios(method, `${hostname}/media/session`, data, {},
      token ? { Authorization: `Bearer ${token}` } : {}, { withCredentials: true });
    return response.data || response;
  }

  public async requestRealtime(method: Method, url: string, data: any = {}, params: any = {}, headers: any = {},
                               timeout: number = 0): Promise<any> {
    return this.request(method, url, data, params, headers, timeout, SERVER_TYPE.SOCKET);
  }

  public async requestEdupad(method: Method, url: string, data: any = {}, params: any = {}, headers: any = {}, 
                            timeout: number = 0): Promise<any> {
    return this.request(method, url, data, params, headers, timeout, SERVER_TYPE.EDUPAD);
  }

  public async requestWbo(method: Method, url: string, data: any = {}, params: any = {}, headers: any = {}, 
                            timeout: number = 0): Promise<any> {
    return this.request(method, url, data, params, headers, timeout, SERVER_TYPE.WBO);
  }

  constructor() {
    this.handleRefresh = true;
  }

  /**
   * This method is used where we are using worker because it doesn't have access to localestorage
   * @param token 
   */
  public setToken(token: string) {
    this.accessToken = token;
    this.handleRefresh = false;
  }
}
export const RequestProvider = new RequestProviderClass();
